import React from "react";
import { ACTIVE_VERSIONS } from "@plugins/gatsby-source-paligo/src/config";
import { H1, Paragraph } from "@singlestore/fusion/components/typography";
import { PathDependentProviders } from "contexts/PathDependentContext";
import { ProductVersionProvider } from "contexts/ProductVersionContext";
import { format } from "date-fns";
import { graphql, PageProps } from "gatsby";

import { BaseLayout } from "layouts/BaseLayout/BaseLayout";
import { DocumentLayout } from "layouts/DocumentLayout/DocumentLayout";

import { JSONParser } from "components/JSONParser/JSONParser";
import { InThisSection } from "components/layout/InThisSection/InThisSection";
import { OnThisPage } from "components/layout/OnThisPage/OnThisPage";
import { WasThisHelpful } from "components/layout/WasThisHelpful/WasThisHelpful";
import { RcWarning } from "components/RcWarning/RcWarning";
import { SEOTags } from "components/SEOTags/SEOTags";
import { Text } from "components/Text/Text";

import {
    DocumentNodeType,
    DocumentVersionInfoType,
    ProductVersionNodeForNavType,
} from "types/types";

import { parseHeaders } from "utils/parse-headers";
import { SITE_URL } from "utils/url";

import "./document.scss";

export type DocumentTemplateProps = {
    documentNode: DocumentNodeType;
    productVersionNode: ProductVersionNodeForNavType;
    allDocumentNode: { otherVersions: Array<DocumentVersionInfoType> };
};

export default function Document({
    data: {
        documentNode,
        productVersionNode,
        allDocumentNode: { otherVersions },
    },
    location: { pathname },
}: PageProps<DocumentTemplateProps>) {
    const { modifiedAt, parsedJsonContent } = documentNode;

    const contentObject = React.useMemo<any>(() => {
        return {
            ...JSON.parse(parsedJsonContent || ""),
            name: "xml",
        };
    }, [parsedJsonContent]);

    const headerTree = React.useMemo<any>(() => {
        return parseHeaders({
            jsonElement: contentObject,
            parentHeader: {
                id: "",
                title: documentNode.name,
                children: [],
            },
        });
    }, [contentObject, documentNode.name]);

    const versionToUrl = React.useMemo<any>(() => {
        // returns a map from a version string (e.g. "db_v8.0") to the url for that version
        return Object.fromEntries(
            otherVersions.map(({ path, version, productNode: { slug } }) => [
                `${slug}_v${version}`,
                path,
            ])
        );
    }, [otherVersions]);

    const rightSidebar = (
        <>
            <OnThisPage headers={headerTree} />
            <WasThisHelpful />
        </>
    );

    const breadcrumbs = [
        ...(documentNode?.breadcrumbs || []),
        { name: documentNode.name, path: documentNode.path },
    ];

    const currentVersion = ACTIVE_VERSIONS.find(
        ({ name }) => name === productVersionNode.name
    );

    let rcWarningToRender;
    if (
        currentVersion?.releaseStatus === "RC" &&
        productVersionNode.productNode.previousActiveVersions.length > 0
    ) {
        rcWarningToRender = <RcWarning versionName={currentVersion.name} />;
    }

    return (
        <PathDependentProviders pathname={pathname}>
            <ProductVersionProvider
                value={{
                    productVersionNode,
                    versionToUrl,
                }}
            >
                <BaseLayout>
                    <DocumentLayout
                        documentNode={documentNode}
                        breadcrumbs={breadcrumbs}
                        rightSidebar={rightSidebar}
                    >
                        <SEOTags
                            title={documentNode.name}
                            baseTitle={`${productVersionNode.productNode.name} Documentation`}
                        >
                            <link
                                rel="canonical"
                                href={`${SITE_URL}${documentNode.canonicalPath}`}
                            />
                        </SEOTags>

                        <main className="document-content">
                            {rcWarningToRender}

                            <H1 variant="heading-6" className="document-title">
                                <Text>{documentNode.name}</Text>
                            </H1>

                            <OnThisPage
                                headers={headerTree}
                                className="on-this-page"
                            />

                            <div className="parsed-document-content">
                                <JSONParser jsonElement={contentObject} />
                            </div>
                        </main>

                        <InThisSection
                            documentNode={documentNode}
                            productVersionNode={productVersionNode}
                        />

                        <Paragraph variant="annotation-1" mt="2x">
                            Last modified:{" "}
                            {format(modifiedAt * 1000, `LLLL d, y`)}
                        </Paragraph>
                    </DocumentLayout>
                </BaseLayout>
            </ProductVersionProvider>
        </PathDependentProviders>
    );
}

export const query = graphql`
    fragment DocumentNodeFields on DocumentNode {
        id
        name
        shortName
        path
        position
        paligoId
    }

    fragment DocumentNodesRecursive on DocumentNode {
        children {
            ...DocumentNodeFields
            children {
                ...DocumentNodeFields
                children {
                    ...DocumentNodeFields
                    children {
                        ...DocumentNodeFields
                    }
                }
            }
        }
    }

    query GetDocumentNodeAndSidebar(
        $id: String!
        $versionNodeId: String!
        $paligoId: Int!
    ) {
        documentNode(id: { eq: $id }) {
            id
            name
            path
            paligoId
            modifiedAt
            parsedJsonContent
            permanentId
            depth
            canonicalPath
            breadcrumbs {
                name
                path
            }
            previousDocumentNode {
                name
                path
            }
            nextDocumentNode {
                name
                path
            }
            children {
                ...DocumentNodeFields
            }
        }

        allDocumentNode(filter: { paligoId: { eq: $paligoId } }) {
            otherVersions: nodes {
                path
                version
                productNode {
                    slug
                }
            }
        }

        productVersionNode(id: { eq: $versionNodeId }) {
            id
            name
            productNode {
                id
                name
                paligoId
                previousActiveVersions
            }
            children {
                ...DocumentNodeFields
                ...DocumentNodesRecursive
            }
        }
    }
`;
